import { designVariables } from "@studio3t/design-variables";
import {
  BadgeWarningSubtle,
  GenericDropdown,
  Icon,
  IconSVG,
  type ListItemProps,
  SVGColorKey,
} from "@studio3t/generic-components";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ConfirmDeleteModal } from "@/components/ConfirmDeleteModal";
import {
  DROPDOWN_TRIGGER_ATTR,
  DropdownOpenReporter,
} from "@/components/dropdownDismissGuard";
import {
  useCreateWorkspace,
  useDeleteWorkspace,
  useServices,
  useUpdateWorkspace,
} from "@/hooks/useApi";
import type { Workspace } from "@/types/api";
import { studioWorkspacePath } from "@/utils/studioRoute";
import {
  DUPLICATE_PIPELINE_MODAL,
  duplicateName,
  SaveWorkspaceModal,
} from "./NewStudio/SaveWorkspaceModal";
import { storedFields } from "./NewStudio/saveWorkspace";
import { ActionsTrigger } from "./PipelineJobActions";

// What a workspace row shares between the lists that show one — the
// Workspaces page and the Pipelines page's Drafts tab: the "…" actions
// menu, the rename / duplicate / delete flows behind it with their modals,
// the "Unresolved" pipeline badge and the service-name lookup the badge
// needs.
const BUTTON = designVariables.ui.button;
const TABLE = designVariables.ui.table;

// The per-row "…" menu (Figma DropdownMenu 13071-38260): Open in Studio /
// Rename / Duplicate, then Delete behind a separator — red bin, regular
// label (the ListItem `error` prop would make the row ignore clicks).
export function WorkspaceActions({
  workspace,
  onOpen,
  onRename,
  onDuplicate,
  onDelete,
  onMenuOpenChange,
}: {
  workspace: Workspace;
  onOpen: (ws: Workspace) => void;
  onRename: (ws: Workspace) => void;
  onDuplicate: (ws: Workspace) => void;
  onDelete: (ws: Workspace) => void;
  /** The menu's open state, for the table's dropdownDismissGuard. */
  onMenuOpenChange?: (open: boolean) => void;
}) {
  const items: ListItemProps[][] = [
    [
      {
        id: "open",
        type: "text",
        value: "open",
        label: "Open in Studio",
        leadingIcon: Icon.Link,
        closeOnSelect: true,
        onClick: () => onOpen(workspace),
      },
      {
        id: "rename",
        type: "text",
        value: "rename",
        label: "Rename",
        leadingIcon: Icon.Edit,
        closeOnSelect: true,
        onClick: () => onRename(workspace),
      },
      {
        id: "duplicate",
        type: "text",
        value: "duplicate",
        label: "Duplicate",
        leadingIcon: Icon.Duplicate,
        closeOnSelect: true,
        onClick: () => onDuplicate(workspace),
      },
    ],
    [
      {
        id: "delete",
        type: "text",
        value: "delete",
        label: "Delete",
        leadingIcon: Icon.Bin,
        leadingIconColorKey: SVGColorKey.errorIcon,
        closeOnSelect: true,
        onClick: () => onDelete(workspace),
      },
    ],
  ];

  return (
    <GenericDropdown
      dropdownItems={items}
      width={{ strategy: "fixed", size: 202 }}
      placement="bottom-end"
    >
      <ActionsTrigger
        type="button"
        aria-label={`Actions for ${workspace.name}`}
        {...DROPDOWN_TRIGGER_ATTR}
      >
        <DropdownOpenReporter onOpenChange={onMenuOpenChange} />
        <IconSVG
          icon={Icon.MoreH}
          size={BUTTON.iconsize}
          svgColorKey={SVGColorKey.semanticTextBody}
        />
      </ActionsTrigger>
    </GenericDropdown>
  );
}

type NameModal = { kind: "rename" | "duplicate"; workspace: Workspace } | null;

// Rename/Duplicate act on the stored workspace record (same as the studio's
// "…" menu); the name modal surfaces a thrown error on its field, and the
// delete confirm stays open with the error on failure.
export function useWorkspaceMutations() {
  const navigate = useNavigate();
  const [nameModal, setNameModal] = useState<NameModal>(null);
  const [pendingDelete, setPendingDelete] = useState<Workspace | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { mutateAsync: updateWorkspace } = useUpdateWorkspace();
  const { mutateAsync: createWorkspace } = useCreateWorkspace();
  const { mutateAsync: deleteWorkspace, isPending: deleting } =
    useDeleteWorkspace();

  // "Duplicate and open" (Figma 15013-126334): the copy opens in the
  // Studio as the draft it is.
  const saveName = async (name: string) => {
    if (!nameModal) return;
    const { kind, workspace } = nameModal;
    if (kind === "rename") {
      await updateWorkspace({
        id: workspace.id,
        payload: { name, ...storedFields(workspace) },
      });
      return;
    }
    const created = await createWorkspace({ name, ...storedFields(workspace) });
    navigate(studioWorkspacePath(created.id));
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleteError(null);
    try {
      await deleteWorkspace(pendingDelete.id);
      setPendingDelete(null);
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : "Failed to delete workspace",
      );
    }
  };

  const closeDelete = () => {
    if (!deleting) {
      setPendingDelete(null);
      setDeleteError(null);
    }
  };

  return {
    nameModal,
    setNameModal,
    pendingDelete,
    setPendingDelete,
    deleteError,
    deleting,
    saveName,
    confirmDelete,
    closeDelete,
  };
}

export type WorkspaceMutations = ReturnType<typeof useWorkspaceMutations>;

export function WorkspaceModals({
  mutations,
}: {
  mutations: WorkspaceMutations;
}) {
  const { nameModal, pendingDelete } = mutations;
  const renaming = nameModal?.kind === "rename";
  return (
    <>
      {nameModal !== null && (
        <SaveWorkspaceModal
          isOpen
          onClose={() => mutations.setNameModal(null)}
          onSave={mutations.saveName}
          {...(renaming ? { title: "Rename draft" } : DUPLICATE_PIPELINE_MODAL)}
          initialName={
            renaming
              ? nameModal.workspace.name
              : duplicateName(nameModal.workspace.name)
          }
          excludeWorkspaceId={renaming ? nameModal.workspace.id : undefined}
          // Renaming a DEPLOYED workspace must not trip over the job it
          // deployed itself — that name is its own.
          excludeJobName={
            renaming ? nameModal.workspace.deployed_job : undefined
          }
        />
      )}

      <ConfirmDeleteModal
        isOpen={pendingDelete !== null}
        title="Delete workspace"
        message={
          <>
            Delete workspace <strong>{pendingDelete?.name}</strong>? This cannot
            be undone.
          </>
        }
        loading={mutations.deleting}
        error={mutations.deleteError}
        onConfirm={mutations.confirmDelete}
        onCancel={mutations.closeDelete}
      />
    </>
  );
}

export function useServiceNames(): Set<string> | null {
  const { data: services } = useServices();
  return useMemo(
    () => (services ? new Set(services.map((s) => s.name)) : null),
    [services],
  );
}

// Badges hug their content and sit at the cell's text inset — same recipe as
// the Pipelines status column: the badge component renders as a block-level
// flex container by default, so the slot forces it inline.
const BadgeSlot = styled.div`
  padding-left: ${TABLE.tbody.cell.gap}px;

  & > * {
    display: inline-flex;
  }
`;

// The "⚠ Unresolved" chip, as on the studio graph's stage cards: the
// design-system warning badge with the icon inlined next to the label.
const UnresolvedBadge = styled(BadgeWarningSubtle)`
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  color: ${(p) => p.theme.badgeWarningSubtleColor};
`;

/** The "⚠ Unresolved" chip in a pipeline cell. */
export function UnresolvedPipelineBadge() {
  return (
    <BadgeSlot>
      <UnresolvedBadge>
        <IconSVG
          icon={Icon.Warning}
          size={11}
          svgColorKey={SVGColorKey.warningIcon}
        />
        Unresolved
      </UnresolvedBadge>
    </BadgeSlot>
  );
}
